import { Component, computed, inject, input, InputSignal, OnInit, output, effect } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NotificationService } from '@features/notifications/services/notification.service';
import { AlertService } from '@shared/services/alert.service';
import { AlertType } from '@utils/enums/alert-type';
import { INotification } from '@features/notifications/models/notification';

@Component({
  selector: 'app-notifications-form',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './notifications-form.component.html',
  styleUrl: './notifications-form.component.css',
})
export class NotificationsFormComponent implements OnInit {
  private readonly notificationSrv: NotificationService = inject(NotificationService);
  private readonly alertSrv: AlertService = inject(AlertService);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

  public notificationToEdit: InputSignal<INotification | null> = input<INotification | null>(null);
  public notificationCreated = output<void>();

  protected notificationForm: FormGroup = new FormGroup({
    mensaje: new FormControl('', Validators.required),
    html:    new FormControl<boolean>(false, { nonNullable: true }),
  });

  protected readonly mensajeValue = toSignal(
    this.notificationForm.get('mensaje')!.valueChanges,
    { initialValue: '' }
  );

  protected readonly safeHtml = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.mensajeValue() ?? '')
  );

  constructor() {
    effect(() => {
      const notification = this.notificationToEdit();
      if (notification) {
        this.notificationForm.patchValue({
          mensaje: notification.mensaje,
          html:    notification.html ?? false,
        });
      } else {
        this.notificationForm.reset({ html: false });
      }
    });
  }

  ngOnInit(): void {}

  onSubmit(): void {
    if (this.notificationForm.valid) {
      const formData = {
        mensaje: this.notificationForm.value.mensaje,
        html:    this.notificationForm.value.html,
      };
      
      if (this.notificationToEdit()) {
        // Modo edición
        this.notificationSrv.updateNotification(this.notificationToEdit()!.secuencia, formData).subscribe({
          next: () => {
            this.alertSrv.showAlert({
              alertType: AlertType.SUCCESS,
              mainMessage: 'Notificación actualizada correctamente',
            });
            this.notificationForm.reset();
            this.notificationCreated.emit();
          },
          error: () => {
            this.alertSrv.showAlert({
              alertType: AlertType.ERROR,
              mainMessage: 'Error al actualizar la notificación',
            });
          },
        });
      } else {
        // Modo creación
        this.notificationSrv.createNotification(formData).subscribe({
          next: () => {
            this.alertSrv.showAlert({
              alertType: AlertType.SUCCESS,
              mainMessage: 'Notificación creada correctamente',
            });
            this.notificationForm.reset();
            this.notificationCreated.emit();
          },
          error: () => {
            this.alertSrv.showAlert({
              alertType: AlertType.ERROR,
              mainMessage: 'Error al crear la notificación',
            });
          },
        });
      }
    }
  }
}
